import { computed, Injectable, linkedSignal, signal } from '@angular/core';
import { BackendService } from './backend/backend.service';
import {
	GetServiceResponse,
	GetTripResponse,
	IAddTripRequest,
	IGetServiceRequest,
	IGetTripRequest,
	IRemoveTripRequest,
	IServiceData,
	ListTripsResponse,
	INaarGoTripRow,
} from './backend/naar-api-client';
import { ModalController, ModalOptions } from '@ionic/angular';
import { StorageService } from './storage.service';
import { AddTripModalComponent } from '../components/modals/add-trip/add-trip-modal.component';
import { TripListModalComponent } from '../components/modals/trip-list/trip-list-modal.component';
import { UrlService } from './url.service';
import { TripHeaderData } from '../types/trip.type';
import { LoadingService } from './loading.service';
import { UserService } from './user.service';

/**
 * Dev notes:
 * Tutti i viaggi vengono salvati nel localStorage
 * Quando un viaggio viene aperto, si cerca nel LS, se non c'è si fa una chiamata al backend e si salva nel LS
 */

@Injectable({
	providedIn: 'root',
})
export class TripService {
	/** List of trips ids and names added by the user. It may includes trips that are not loaded and should be fetched */
	public remoteTripsHeaders = new Map<number, TripHeaderData>();

	public selectedTripId$ = signal<number | null>(null);

	public selectedTripHeader$ = computed(() => {
		return this.remoteTripsHeaders.get(this.selectedTripId$());
	});

	public selectedTrip$ = linkedSignal(() => {
		return this.storage.getTrip(this.selectedTripId$()) || null;
	});

	constructor(
		private backend: BackendService,
		private modalCtrl: ModalController,
		private storage: StorageService,
		private urlService: UrlService,
		private loading: LoadingService,
	) {
	}

	public async init() {
		await this.loading.start();
		if (this.storage.isNetworkConnected$()) {
			const localTripsIds = this.storage.getTripsIds();

			const response = await this.backend.post<ListTripsResponse>('trip/list', {});
			const tripHeaders = response.tripHeaders || [];

			//* Cast as TripHeaderData and set the icon based on the local storage
			this.remoteTripsHeaders = new Map(
				tripHeaders.map(header => {
					const icon = localTripsIds.includes(header.id) ? 'download-outline' : 'cloud-download-outline';
					return [header.id, new TripHeaderData(header.id, header.name, icon)];
				})
			);

			//* Remove local trips that are not in the list
			const idsToRemove = localTripsIds.filter(id => !this.remoteTripsHeaders.has(id));
			for (const tripId of idsToRemove) {
				await this.storage.removeTrip(tripId);
			}
		} else {
			const localTrips = this.storage.getTrips();
			this.remoteTripsHeaders = new Map(
				localTrips.map(trip =>
					[trip.id, new TripHeaderData(trip.id, trip.description, 'cloud-offline-outline')]
				)
			);
		}

		const lastOpenedTripId = await this.storage.getLastOpenedTripId();
		if (this.storage.hasTrip(lastOpenedTripId)) {
			await this.changeTrip(lastOpenedTripId);
		}
		else {
			await this.changeTrip(null);
		}
		await this.loading.stop();
	}

	/** The selected trip id. When set, the selected trip is loaded only if needed, does not force a reload */
	public async changeTrip(id: number) {
		if (id && !this.storage.hasTrip(id)) {
			await this.fetchTrip(id);
		}
		else if (id && this.storage.isNetworkConnected$() && this.storage.isTripCacheStale(id)) {
			//* Migrazione: il viaggio in cache è stato scaricato con uno schema più vecchio
			//* (prima di serviceId sugli step, di passengers, ecc.) → si riscarica una volta sola
			try {
				await this.fetchTrip(id);
			} catch {
				//* Se il refetch fallisce si continua con la copia in cache
			}
		}
		this.selectedTripId$.set(id);
		this.storage.setLastOpenedTripId(id);
	}

	/** Fetch the trip and update the storage and the headers set */
	public async fetchTrip(tripId: number = this.selectedTripId$()): Promise<void> {
		await this.loading.start();
		try {
			const request: IGetTripRequest = {
				tripId: tripId,
			};
			const getTripResponse = await this.backend.post<GetTripResponse>('trip/get', request);
			const trip = getTripResponse.trip as INaarGoTripRow;

			const newHeader = new TripHeaderData(trip.id, trip.description, 'download-outline');
			this.remoteTripsHeaders.set(trip.id, newHeader);
			this.selectedTrip$.set(trip);
			await this.storage.setTrip(trip);
		} finally {
			await this.loading.stop();
		}
	}

	/** Fetch the published data of the service behind a trip step (localized by the backend with the user language) */
	public async fetchServiceData(serviceId: number): Promise<IServiceData> {
		await this.loading.start();
		try {
			const request: IGetServiceRequest = {
				serviceId: serviceId,
			};
			const response = await this.backend.post<GetServiceResponse>('trip/service/get', request);
			return response.service;
		} finally {
			await this.loading.stop();
		}
	}

	//#region Modals

	/** Su mobile le modali si presentano come bottom-sheet; su desktop restano centrate */
	private sheetModalOptions(initialBreakpoint: number): Partial<ModalOptions> {
		if (!this.storage.isMobile) return {};
		return {
			breakpoints: [0, initialBreakpoint, 0.9],
			initialBreakpoint: initialBreakpoint,
			handle: true,
			cssClass: 'nr-sheet-modal',
		};
	}

	public async openAddTripModal(tripListModal: ModalController = null): Promise<void> {
		const modal = await this.modalCtrl.create({
			component: AddTripModalComponent,
			...this.sheetModalOptions(0.55),
		});
		await modal.present();

		const { data, role } = await modal.onWillDismiss();
		const tripId = parseInt(data);

		if (role !== 'confirm' || !tripId) {
			return;
		}

		await this.addTrip(tripId);

		//* If this modal was opened from the trip list modal, we dismiss that modal too
		if (tripListModal) {
			tripListModal.dismiss(null, 'cancel');
		}
	}

	public async addTrip(tripId: number, displayError: boolean = true) {
		const request: IAddTripRequest = {
			tripId: tripId,
		};
		await this.backend.post('trip/add', request, displayError);

		//* Change the trip to the one just added.
		//* It will be fetched and set in the storage inside the changeTrip method
		await this.changeTrip(tripId);
	}

	public async openTripListModal(): Promise<void> {
		const modal = await this.modalCtrl.create({
			component: TripListModalComponent,
			...this.sheetModalOptions(0.65),
		});
		await modal.present();

		const { data, role } = await modal.onWillDismiss();
		const tripId = parseInt(data);

		if (role !== 'confirm' || !tripId) {
			return;
		}

		await this.changeTrip(tripId);
		this.urlService.get('trip').navigate();
	}
	//#endregion Modals

	public async removeTrip(tripId: number): Promise<void> {
		const request: IRemoveTripRequest = {
			tripId: tripId,
		};
		await this.backend.post('trip/remove', request);

		await this.storage.removeTrip(tripId);
		this.remoteTripsHeaders.delete(tripId);

		const lastOpenedTripId = await this.storage.getLastOpenedTripId();
		await this.changeTrip(lastOpenedTripId);
	}
}
